// Import jQuery for this module
import $ from 'jquery';

// Import LiveChat module for on-demand chat functionality
import { initLiveChat } from '@scripts/livechat.js';
import { initCookieBanner } from '@scripts/cookie-banner.js';
import { initAnalyticsLoader } from '@scripts/analytics-loader.js';

// Header Menu Click
function headerMenuClick() {
  if ($('.header__menu-btn').length > 0) {
    let scrollPosition = 0;

    // Function to prevent scrolling on iOS
    function preventScroll(e) {
      e.preventDefault();
    }

    // Function to lock body scroll
    function lockBodyScroll() {
      // Save current scroll position
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      const { body } = document;
      const { documentElement: html } = document;

      // Apply styles using native JavaScript for better iOS compatibility
      body.style.position = 'fixed';
      body.style.top = `-${scrollPosition}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';

      // Also apply to html element for extra iOS safety
      html.style.overflow = 'hidden';

      // Prevent touchmove on body (iOS specific fix)
      document.body.addEventListener('touchmove', preventScroll, { passive: false });
    }

    // Function to unlock body scroll
    function unlockBodyScroll() {
      const { body } = document;
      const { documentElement: html } = document;

      // Remove all inline styles
      body.style.overflow = '';
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';

      html.style.overflow = '';

      // Remove touchmove listener
      document.body.removeEventListener('touchmove', preventScroll, { passive: false });

      // Restore scroll position
      window.scrollTo(0, scrollPosition);
    }

    // Focus trap for mobile menu
    function getFocusableElements() {
      // Get all focusable elements in the menu in the correct order
      const $menuLinks = $('.header__primary-menu a');
      const $bookACallBtn = $('.header__book-a-call.is-mobile .l-btn');
      const $logo = $('.header__logo');
      const $closeBtn = $('.header__menu-close-btn.is-mobile');

      // Combine all focusable elements in the desired order
      return [
        ...$menuLinks.toArray(),
        ...$bookACallBtn.toArray(),
        ...$logo.toArray(),
        ...$closeBtn.toArray(),
      ].filter((el) => {
        // Filter out hidden elements (like header__menu-buttons.is-hidden)
        const $el = $(el);
        return (
          $el.is(':visible') && !$el.closest('.is-hidden').length && !$el.hasClass('is-hidden')
        );
      });
    }

    // Handle Tab key navigation for focus trapping
    $(document).on('keydown.focusTrap', (e) => {
      const $header = $('header');
      const isMenuOpen = $header.hasClass('is-active');

      if (!isMenuOpen || e.key !== 'Tab') {
        return;
      }

      const focusableElements = getFocusableElements();

      if (focusableElements.length === 0) {
        return;
      }

      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (currentIndex === -1) {
        // If focus is outside menu, focus first element
        e.preventDefault();
        focusableElements[0]?.focus();
        return;
      }

      if (e.shiftKey) {
        // Shift + Tab: go to previous element (loop to last if at first)
        e.preventDefault();
        const prevIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
        focusableElements[prevIndex]?.focus();
      } else {
        // Tab: go to next element (loop to first if at last)
        e.preventDefault();
        const nextIndex = currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
        focusableElements[nextIndex]?.focus();
      }
    });

    $('.header__menu-btn, .header__menu-close-btn').on('click', (e) => {
      e.preventDefault();
      const $header = $('header');
      const $headerMenuBtn = $('.header__menu-buttons');
      const $main = $('#main-content');
      const $footer = $('footer');

      $header.toggleClass('is-active');
      $headerMenuBtn.toggleClass('is-hidden');
      const isActive = $header.hasClass('is-active');
      $('.header__menu-btn').attr('aria-expanded', isActive);

      // Prevent body scroll and hide main content from screen readers when menu is open
      if (isActive) {
        lockBodyScroll();
        $main.attr('aria-hidden', 'true');
        $footer.attr('aria-hidden', 'true');
        // Focus first menu link when menu opens
        setTimeout(() => {
          const $firstMenuLink = $('.header__primary-menu a').first();
          if ($firstMenuLink.length) {
            $firstMenuLink.focus();
          }
        }, 100);
      } else {
        unlockBodyScroll();
        $main.removeAttr('aria-hidden');
        $footer.removeAttr('aria-hidden');
      }
    });

    // Also handle when menu closes via ESC key
    $(document).on('keydown', (e) => {
      if (e.key === 'Escape') {
        const $header = $('header');
        if ($header.hasClass('is-active')) {
          $('.header__menu-close-btn').trigger('click');
        }
      }
    });
  }
}

// Convert Images to SVG
function imgToSvg() {
  const convertImages = (_query, callback) => {
    const images = document.querySelectorAll('img.svg');
    images.forEach((image) => {
      fetch(image.src)
        .then((res) => res.text())
        .then((data) => {
          const parser = new DOMParser();
          const svg = parser.parseFromString(data, 'image/svg+xml').querySelector('svg');

          // Check if SVG was successfully parsed
          if (!svg) {
            return;
          }

          svg.setAttribute(
            'viewBox',
            `0 0 ${svg.getAttribute('width')} ${svg.getAttribute('height')}`
          );
          // Set preserveAspectRatio to maintain aspect ratio for better accessibility
          // Use "none" only if the image had a data attribute indicating it should stretch
          const shouldStretch = image.getAttribute('data-stretch') === 'true';
          svg.setAttribute('preserveAspectRatio', shouldStretch ? 'none' : 'xMidYMid meet');
          if (image.id) {
            svg.id = image.id;
          }
          // Copy all class names from the image to the svg
          image.classList.forEach((cls) => {
            svg.classList.add(cls);
          });

          // Handle accessibility attributes
          const altText = image.getAttribute('alt');
          const ariaHidden = image.getAttribute('aria-hidden');
          if (ariaHidden === 'true' || !altText) {
            // Decorative image - hide from screen readers
            svg.setAttribute('aria-hidden', 'true');
            svg.setAttribute('focusable', 'false');
          } else {
            // Informative image - provide accessible name
            const titleId = `svg-title-${Math.random().toString(36).substring(2, 9)}`;
            const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
            title.id = titleId;
            title.textContent = altText;
            svg.prepend(title);
            svg.setAttribute('role', 'img');
            svg.setAttribute('aria-labelledby', titleId);
          }

          // Add vector-effect="non-scaling-stroke" to all <path> elements within the SVG
          const paths = svg.querySelectorAll('path');
          paths.forEach((path) => {
            path.setAttribute('vector-effect', 'non-scaling-stroke');
          });

          image.parentNode.replaceChild(svg, image);
        })
        .then(callback);
    });
  };
  convertImages('img');
}

// Current Year
function currentYear() {
  if ($('#year').length > 0) {
    const date = new Date();
    const year = date.getFullYear();
    document.getElementById('year').innerHTML = year;
  }
}

// Active Navigation State
function activeNavigation() {
  if ($('.header__primary-menu').length === 0) {
    return;
  }

  const $navLinks = $('.header__primary-menu a');

  // Get current page path
  const currentPath = window.location.pathname;

  // Normalize current path: remove .html extension and trailing slash
  // Handle home page cases: '', '/', '/index', '/index.html'
  let normalizedPath =
    currentPath
      .replace(/\/index\.html$/, '')
      .replace(/\/index$/, '')
      .replace(/\.html$/, '')
      .replace(/\/$/, '') || '/';

  // Ensure home page is represented as '/'
  if (normalizedPath === '' || normalizedPath === '/index') {
    normalizedPath = '/';
  }

  $navLinks.each(function () {
    const $link = $(this);
    const linkHref = $link.attr('href');

    if (!linkHref) {
      return;
    }

    // Extract pathname from href (handles both full URLs and relative paths)
    let linkPath;
    try {
      // If it's a full URL, parse it to get the pathname
      if (
        linkHref.startsWith('http://') ||
        linkHref.startsWith('https://') ||
        linkHref.startsWith('//')
      ) {
        const url = new URL(linkHref, window.location.origin);
        linkPath = url.pathname;
      } else {
        linkPath = linkHref;
      }
    } catch {
      // If parsing fails, use the href as is
      linkPath = linkHref;
    }

    // Normalize link path (remove .html extension and trailing slash)
    let normalizedLinkPath =
      linkPath
        .replace(/\/index\.html$/, '')
        .replace(/\/index$/, '')
        .replace(/\.html$/, '')
        .replace(/\/$/, '') || '/';

    // Ensure home page link is represented as '/'
    if (normalizedLinkPath === '' || normalizedLinkPath === '/index') {
      normalizedLinkPath = '/';
    }

    // Check if paths match
    let isActive = false;

    if (normalizedPath === normalizedLinkPath) {
      // Exact match
      isActive = true;
    } else if (normalizedLinkPath !== '/' && normalizedPath.startsWith(`${normalizedLinkPath}/`)) {
      // Current path starts with link path (for subpages)
      isActive = true;
    }

    // Add or remove is-active class
    if (isActive) {
      $link.addClass('is-active');
      $link.attr('aria-current', 'page');
    } else {
      $link.removeClass('is-active');
      $link.removeAttr('aria-current');
    }
  });
}

// Helper function to scroll to element accounting for sticky header with slower animation
function scrollToElement(element, offset = 0, duration = 1000) {
  if (!element) {
    return;
  }

  const header = document.querySelector('.ally.header');
  let headerHeight = 0;

  if (header) {
    const headerRect = header.getBoundingClientRect();
    headerHeight = headerRect.height;
  }

  const elementRect = element.getBoundingClientRect();
  const elementTop = elementRect.top + window.pageYOffset;
  const targetPosition = elementTop - headerHeight - offset;

  if (duration <= 0) {
    window.scrollTo({ top: Math.max(0, targetPosition), left: 0, behavior: 'auto' });
    return;
  }

  const startPosition = window.pageYOffset;
  const distance = targetPosition - startPosition;
  let startTime = null;

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function animation(currentTime) {
    if (startTime === null) {
      startTime = currentTime;
    }
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);
    const ease = easeInOutCubic(progress);

    window.scrollTo(0, startPosition + distance * ease);

    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    }
  }

  requestAnimationFrame(animation);
}

/**
 * Credit Unions landing: intercept "How can we help" hash links so targets align below
 * the sticky header using measured height (CSS scroll-margin is inconsistent across browsers).
 * Honors prefers-reduced-motion; corrects scroll on initial load when URL contains a hash.
 */
const CU_HASH_GAP_PX = 8;
const CU_HASH_DURATION_MS = 450;
function initCreditUnionsHashNavigation() {
  if (!$('body').hasClass('page-credit-union-accessibility')) {
    return;
  }

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getCreditUnionsAnnouncementRegion() {
    let region = document.getElementById('cu-in-page-announcement');

    if (region) {
      return region;
    }

    region = document.createElement('div');
    region.id = 'cu-in-page-announcement';
    region.className = 'cu-in-page-announcement sr-only';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);

    return region;
  }

  function getCreditUnionsFocusTarget(target) {
    if (!target) {
      return null;
    }

    const labelledById = target.getAttribute('aria-labelledby');
    if (labelledById) {
      const labelledByTarget = document.getElementById(labelledById);
      if (labelledByTarget) {
        return labelledByTarget;
      }
    }

    return target.querySelector('h1, h2, h3, h4, h5, h6') || target;
  }

  function focusCreditUnionsTarget(target, duration) {
    const focusTarget = getCreditUnionsFocusTarget(target);
    if (!focusTarget) {
      return;
    }

    const focusDelay = duration > 0 ? duration + 100 : 0;

    window.setTimeout(() => {
      const hadTabindex = focusTarget.hasAttribute('tabindex');
      const hadFocusClass = focusTarget.classList.contains('cu-in-page-focus-target');
      if (!hadTabindex) {
        focusTarget.setAttribute('tabindex', '-1');
      }
      if (!hadFocusClass) {
        focusTarget.classList.add('cu-in-page-focus-target');
      }

      focusTarget.focus({ preventScroll: true });

      if (!hadTabindex || !hadFocusClass) {
        focusTarget.addEventListener(
          'blur',
          () => {
            if (!hadTabindex) {
              focusTarget.removeAttribute('tabindex');
            }
            if (!hadFocusClass) {
              focusTarget.classList.remove('cu-in-page-focus-target');
            }
          },
          { once: true }
        );
      }
    }, focusDelay);
  }

  function announceCreditUnionsTarget(target, duration) {
    const focusTarget = getCreditUnionsFocusTarget(target);
    const announcementRegion = getCreditUnionsAnnouncementRegion();
    const targetLabel = focusTarget?.textContent?.replace(/\s+/g, ' ').trim();

    if (!announcementRegion || !targetLabel) {
      return;
    }

    const announceDelay = duration > 0 ? duration + 100 : 0;

    window.setTimeout(() => {
      announcementRegion.textContent = '';
      window.requestAnimationFrame(() => {
        announcementRegion.textContent = `Jumped to ${targetLabel}`;
      });
    }, announceDelay);
  }

  function scrollToCreditUnionsTarget(el, instant = false) {
    if (!el) {
      return 0;
    }
    const duration = instant || prefersReducedMotion() ? 0 : CU_HASH_DURATION_MS;
    scrollToElement(el, CU_HASH_GAP_PX, duration);
    return duration;
  }

  $('.how-can-we-help-cu').on('click', 'a[href^="#"]', function (e) {
    const href = $(this).attr('href');
    if (!href || href === '#' || href.length < 2) {
      return;
    }
    const id = href.slice(1);
    const target = document.getElementById(id);
    if (!target) {
      return;
    }
    e.preventDefault();
    const duration = scrollToCreditUnionsTarget(target);
    focusCreditUnionsTarget(target, duration);
    announceCreditUnionsTarget(target, duration);
    if (history.pushState) {
      history.pushState(null, '', href);
    } else {
      window.location.hash = href;
    }
  });

  function fixHashScrollOnLoad() {
    const { hash } = window.location;
    if (!hash || hash.length < 2) {
      return;
    }
    const target = document.getElementById(hash.slice(1));
    if (!target) {
      return;
    }
    scrollToCreditUnionsTarget(target, true);
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(fixHashScrollOnLoad);
  });
}

const ADA_RESPONSE_FORM_SECTION_ID = 'ada-response-form-section';
const ADA_IN_PAGE_HASH_GAP_PX = 8;
const ADA_IN_PAGE_HASH_DURATION_MS = 750;

/** ADA lawsuit page: smooth-scroll "Get Help Now" links to the response form section. */
function initAdaLawsuitInPageNavigation() {
  if (!$('body').hasClass('page-ada-lawsuit')) {
    return;
  }

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function scrollToAdaResponseFormSection(instant = false) {
    const target = document.getElementById(ADA_RESPONSE_FORM_SECTION_ID);
    if (!target) {
      return 0;
    }
    const duration = instant || prefersReducedMotion() ? 0 : ADA_IN_PAGE_HASH_DURATION_MS;
    scrollToElement(target, ADA_IN_PAGE_HASH_GAP_PX, duration);
    return duration;
  }

  function focusAdaResponseFormHeading(duration) {
    const heading = document.getElementById('ada-response-form-title');
    if (!heading) {
      return;
    }
    const focusDelay = duration > 0 ? duration + 100 : 0;
    window.setTimeout(() => {
      const hadTabindex = heading.hasAttribute('tabindex');
      if (!hadTabindex) {
        heading.setAttribute('tabindex', '-1');
      }
      heading.focus({ preventScroll: true });
      if (!hadTabindex) {
        heading.addEventListener(
          'blur',
          () => {
            heading.removeAttribute('tabindex');
          },
          { once: true }
        );
      }
    }, focusDelay);
  }

  $(document).on('click', `a[href="#${ADA_RESPONSE_FORM_SECTION_ID}"]`, (e) => {
    e.preventDefault();
    const duration = scrollToAdaResponseFormSection();
    focusAdaResponseFormHeading(duration);
    const hash = `#${ADA_RESPONSE_FORM_SECTION_ID}`;
    if (history.pushState) {
      history.pushState(null, '', hash);
    } else {
      window.location.hash = hash;
    }
  });

  function fixAdaHashScrollOnLoad() {
    const { hash } = window.location;
    if (hash !== `#${ADA_RESPONSE_FORM_SECTION_ID}`) {
      return;
    }
    scrollToAdaResponseFormSection(true);
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(fixAdaHashScrollOnLoad);
  });
}

// Skip to main content functionality
function skipToMainContent() {
  if ($('.skip-content').length > 0) {
    $('.skip-content').on('click', (e) => {
      e.preventDefault();

      const targetId = $(e.currentTarget).attr('href');
      const $target = $(targetId);

      if ($target.length) {
        // Scroll to the target smoothly accounting for header height with slower animation
        scrollToElement($target[0], 20, 1000);

        // Then focus after scroll completes (to avoid double announcement)
        const scrollDuration = 1000;
        const focusBuffer = 100;
        setTimeout(() => {
          // Focus the target element
          $target.attr('tabindex', '-1');
          $target.focus();

          // Remove the outline after blur (optional for better UX)
          $target.one('blur', function () {
            $(this).removeAttr('tabindex');
          });
        }, scrollDuration + focusBuffer);
      }
    });
  }
}

const FAQ_ACCORDION_DURATION = 300;
function faqAccordion() {
  const accordionConfigs = [
    {
      root: '.faq-accordion',
      trigger: '.faq-accordion__trigger',
      item: '.faq-accordion__item',
      panel: '.faq-accordion__panel',
    },
    {
      root: '.cu-common-questions',
      trigger: '.cu-common-questions__trigger',
      item: '.cu-common-questions__item',
      panel: '.cu-common-questions__panel',
    },
  ];

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const duration = prefersReducedMotion ? 0 : FAQ_ACCORDION_DURATION;

  accordionConfigs.forEach((config) => {
    const $accordions = $(config.root);
    if ($accordions.length === 0) {
      return;
    }

    // One delegated listener per accordion container (Rule #361); scope all logic to that instance.
    $accordions.each(function () {
      const $accordion = $(this);
      $accordion.on('click', config.trigger, function (e) {
        e.preventDefault();
        const $trigger = $(this);
        const $item = $trigger.closest(config.item);
        const $panel = $item.find(config.panel);
        const isExpanded = $item.hasClass('is-expanded');

        if (isExpanded) {
          // Switch icon to + before slideUp; is-closing keeps panel visible so animation runs
          $item.removeClass('is-expanded').addClass('is-closing');
          $trigger.attr('aria-expanded', 'false');
          $panel.stop(true, true).slideUp(duration, () => {
            $item.removeClass('is-closing');
            $panel.attr('aria-hidden', 'true');
          });
        } else {
          // Close other items only after each panel’s slideUp completes so CSS display:none doesn’t cancel the animation.
          // Only close other items that are expanded (slideUp on already-hidden panels would flash them open)
          $accordion
            .find(`${config.item}.is-expanded`)
            .not($item)
            .each(function () {
              const $otherItem = $(this);
              const $otherPanel = $otherItem.find(config.panel);
              const $otherTrigger = $otherItem.find(config.trigger);
              $otherItem.removeClass('is-expanded').addClass('is-closing');
              $otherTrigger.attr('aria-expanded', 'false');
              $otherPanel.stop(true, true).slideUp(duration, () => {
                $otherItem.removeClass('is-closing');
                $otherPanel.attr('aria-hidden', 'true');
              });
            });
          $item.addClass('is-opening');
          $trigger.attr('aria-expanded', 'true');
          $panel.attr('aria-hidden', 'false');
          $panel.stop(true, true).slideDown(duration, () => {
            $item.removeClass('is-opening').addClass('is-expanded');
          });
        }
      });
    });
  });
}

/**
 * Measures the hero heading segment used for the white background via DOM Range.
 * Default: content before the first `<br>`, else full heading text.
 * With `data-hero-bg-measure="before-accent"`: content before `.hero-banner__title-accent`.
 *
 * @param {HTMLElement} headingElement - The visible `.hero-banner__title.has-bg` node
 * @returns {{ width: number, height: number, useMeasuredHeight: boolean }}
 */
function getHeroHeadingMeasuredWidth(headingElement) {
  const accentSpan = headingElement.querySelector('.hero-banner__title-accent');
  const measureBeforeAccent =
    headingElement.dataset.heroBgMeasure === 'before-accent' && accentSpan;
  const firstBr = headingElement.querySelector('br');
  const range = document.createRange();

  if (measureBeforeAccent) {
    range.setStart(headingElement, 0);
    range.setEndBefore(accentSpan);
  } else if (firstBr) {
    range.setStart(headingElement, 0);
    range.setEndBefore(firstBr);
  } else {
    range.selectNodeContents(headingElement);
  }

  const { width, height } = range.getBoundingClientRect();
  return {
    width: width || 0,
    height: height || 0,
    useMeasuredHeight: Boolean(measureBeforeAccent),
  };
}

/**
 * Gets the responsive extra padding value from CSS custom property.
 *
 * @param {jQuery} $content - The jQuery content container element
 * @returns {number} The extra padding value in pixels (defaults to 32 if not found)
 */
function getExtraPadding($content) {
  const [contentElement] = $content;
  const paddingComputedStyle = window.getComputedStyle(contentElement);
  const extraPaddingValue = paddingComputedStyle.getPropertyValue('--bg-extra-padding').trim();
  return extraPaddingValue ? parseFloat(extraPaddingValue) : 32; // Fallback to mobile default
}

/**
 * Applies measured dimensions to the background element via CSS custom properties.
 *
 * @param {jQuery} $bgDiv - The jQuery background div element
 * @param {number} width - The width value in pixels to apply
 * @param {number|null} height - Optional height in pixels (--bg-height); omitted values clear the override
 */
function applyBackgroundWidth($bgDiv, width, height = null) {
  if (!width || width <= 0) {
    return;
  }

  const [bgDivElement] = $bgDiv;
  bgDivElement.style.setProperty('--bg-width', `${width}px`);

  if (height !== null && height > 0) {
    bgDivElement.style.setProperty('--bg-height', `${height}px`);
  } else {
    bgDivElement.style.removeProperty('--bg-height');
  }
}

/**
 * Checks if the current viewport is at or above the md breakpoint (768px).
 *
 * @returns {boolean} True if viewport width >= 768px, false otherwise
 */
function isMdViewport() {
  return window.innerWidth >= 768;
}

/**
 * Measures the width of heading text before the first line break and sets a CSS custom property
 * to dynamically size a background element. This enables responsive background shapes that match
 * text width across different viewport sizes.
 *
 * Process:
 * 1. Determines viewport size (md breakpoint is 768px)
 * 2. Selects appropriate heading element (.is-desktop for >= md, .is-mobile for < md)
 *    - If both .is-desktop and .is-mobile versions exist, selects based on viewport
 *    - If only one .has-bg exists (without responsive classes), uses that one
 * 3. Measures width before the first `<br>` via a DOM Range when possible, else hidden-span text width
 * 4. Falls back to measuring extracted text with a temporary hidden element when Range width is unavailable
 * 5. Adds responsive padding from CSS custom property --bg-extra-padding
 * 6. Sets the calculated width as --bg-width CSS custom property on the sibling .hero-banner__bg element
 * 7. Adds is-ready class to .hero-banner__content after a delay for fade-in animation
 *
 * Dependencies:
 * - Requires elements with class .has-bg (the heading to measure)
 * - May have both .is-desktop.has-bg and .is-mobile.has-bg elements (responsive headings)
 * - Requires a sibling element with class .hero-banner__bg (the background element)
 * - Requires a parent element with class .hero-banner__content (for CSS custom property access)
 * - Reads CSS custom property --bg-extra-padding from .hero-banner__content for responsive padding
 *
 * Side Effects:
 * - Sets --bg-width CSS custom property on .hero-banner__bg elements
 * - Adds is-ready class to .hero-banner__content elements after 200ms delay
 * - Temporarily appends and removes a hidden span element to the body for measurement
 */
function backgroundColorForHeading() {
  setTimeout(() => {
    $('.hero-banner__content').addClass('is-ready');
  }, 200);

  // Find all hero banner content containers
  $('.hero-banner__content').each(function () {
    const $content = $(this);
    const $bgDiv = $content.find('.hero-banner__bg');

    if ($bgDiv.length === 0) {
      return;
    }

    // Check if there are both desktop and mobile versions
    const $desktopHeading = $content.find('.hero-banner__title.is-desktop.has-bg');
    const $mobileHeading = $content.find('.hero-banner__title.is-mobile.has-bg');
    const hasResponsiveVersions = $desktopHeading.length > 0 && $mobileHeading.length > 0;

    let $heading;

    if (hasResponsiveVersions) {
      // Select the appropriate heading based on viewport size
      // md breakpoint is 768px
      const isMd = isMdViewport();
      $heading = isMd ? $desktopHeading : $mobileHeading;
    } else {
      // Fallback: use any heading with .has-bg class (for banners without responsive versions)
      $heading = $content.find('.hero-banner__title.has-bg, h1.has-bg');
    }

    if ($heading.length === 0) {
      return;
    }

    const [headingElement] = $heading;

    const {
      width: textWidth,
      height: textHeight,
      useMeasuredHeight,
    } = getHeroHeadingMeasuredWidth(headingElement);
    if (!textWidth) {
      return;
    }

    // Get responsive extra padding from CSS custom property
    const extraPadding = getExtraPadding($content);

    // Calculate total width and apply to background element
    const totalWidth = textWidth + extraPadding;
    applyBackgroundWidth($bgDiv, totalWidth, useMeasuredHeight ? textHeight : null);
  });
}

/**
 * Sets up observers to watch for style changes on hero banner headings
 * and automatically triggers backgroundColorForHeading() when letter-spacing or
 * other font-related properties change. This is useful when CSS is modified by
 * browser extensions or other external scripts.
 *
 * Uses ResizeObserver and MutationObserver for efficient, event-driven detection
 * instead of polling, which improves performance and battery life.
 */
function watchForHeadingStyleChanges() {
  // Debounce function to prevent excessive calls
  let updateTimer;
  const debounceDelay = 100;

  function triggerUpdate() {
    clearTimeout(updateTimer);
    updateTimer = setTimeout(() => {
      backgroundColorForHeading();
    }, debounceDelay);
  }

  // Watch for style attribute changes on heading elements
  const mutationObserver = new MutationObserver((mutations) => {
    let shouldCheck = false;

    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        const { target } = mutation;
        // Check if it's a heading element or its parent container
        if (
          target.classList?.contains('hero-banner__title') ||
          target.classList?.contains('hero-banner__content') ||
          target.closest?.('.hero-banner__content')
        ) {
          // Check if style attribute or class was changed
          if (mutation.attributeName === 'style' || mutation.attributeName === 'class') {
            shouldCheck = true;
          }
        }
      } else if (mutation.type === 'childList') {
        // New headings might have been added
        const addedNodes = Array.from(mutation.addedNodes);
        if (
          addedNodes.some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              (node.classList?.contains('hero-banner__title') ||
                node.querySelector?.('.hero-banner__title'))
          )
        ) {
          shouldCheck = true;
        }
      }
    });

    if (shouldCheck) {
      triggerUpdate();
    }
  });

  // Use ResizeObserver to detect when heading elements resize
  // This efficiently catches letter-spacing changes that affect layout width
  const resizeObserver = new ResizeObserver((entries) => {
    // Only trigger if the resize is likely due to text width changes
    // (not just viewport resizing, which is already handled by window resize)
    let hasTextWidthChange = false;

    entries.forEach((entry) => {
      const { target } = entry;
      // Check if this is a heading element or within a hero banner
      if (
        target.classList?.contains('hero-banner__title') ||
        target.closest?.('.hero-banner__content')
      ) {
        // Check if width changed (letter-spacing affects width)
        const { width } = entry.contentRect;
        const previousWidth = target._previousWidth;

        if (previousWidth !== undefined && previousWidth !== width) {
          hasTextWidthChange = true;
        }

        target._previousWidth = width;
      }
    });

    if (hasTextWidthChange) {
      triggerUpdate();
    }
  });

  // Helper function to set up observers on a hero banner element
  function setupObserversForBanner(contentElement) {
    // MutationObserver for attribute changes
    mutationObserver.observe(contentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
      subtree: true,
    });

    // ResizeObserver for layout changes (catches letter-spacing changes)
    resizeObserver.observe(contentElement);

    // Also observe individual headings
    $(contentElement)
      .find('.hero-banner__title.has-bg')
      .each(function () {
        resizeObserver.observe(this);
      });
  }

  // Start observing when DOM is ready
  $(() => {
    // Observe all existing hero banner content containers and headings
    $('.hero-banner__content').each(function () {
      setupObserversForBanner(this);
    });

    // Use a targeted observer to watch for new hero banners being added
    // This is more efficient than observing the entire document.body
    const dynamicContentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node is a hero banner or contains one
              if (node.classList?.contains('hero-banner__content')) {
                setupObserversForBanner(node);
                // Trigger update for the new banner
                triggerUpdate();
              } else {
                // Check if it contains hero banners
                const heroBanners = node.querySelectorAll?.('.hero-banner__content');
                if (heroBanners) {
                  heroBanners.forEach((banner) => {
                    setupObserversForBanner(banner);
                  });
                  if (heroBanners.length > 0) {
                    triggerUpdate();
                  }
                }
              }
            }
          });
        }
      });
    });

    // Observe the main content area instead of entire body
    // This is more targeted and performant
    const mainContent = document.getElementById('main-content') || document.body;
    dynamicContentObserver.observe(mainContent, {
      childList: true,
      subtree: true,
    });

    // Listen for custom events that extensions can dispatch
    // This is the recommended way for external scripts (like browser extensions) to trigger recalculation
    // Extensions can trigger: document.dispatchEvent(new CustomEvent('ally:recalculate-heading-bg'))
    document.addEventListener('ally:recalculate-heading-bg', triggerUpdate);

    // Also listen for CSS custom property changes (if extensions modify CSS variables)
    // This is a fallback for when extensions modify CSS rules directly
    const styleSheetObserver = new MutationObserver(() => {
      // When stylesheets change, check if it affects our headings
      triggerUpdate();
    });

    // Watch for stylesheet additions/removals
    styleSheetObserver.observe(document.head, {
      childList: true,
      subtree: false,
    });
  });
}

function processGridParaHeight() {
  const $processGridItems = $('.process-grid__item');

  if ($processGridItems.length === 0) {
    return;
  }

  // Reset heights first to get accurate measurements
  $processGridItems.find('.process-grid__item-text').css('height', '');

  // Find the maximum height
  let maxHeight = 0;

  $processGridItems.each(function () {
    const $textElement = $(this).find('.process-grid__item-text');
    if ($textElement.length > 0) {
      const height = $textElement.outerHeight();
      if (height > maxHeight) {
        maxHeight = height;
      }
    }
  });

  // Apply the maximum height to all text elements
  if (maxHeight > 0) {
    $processGridItems.find('.process-grid__item-text').css('height', `${maxHeight}px`);
  }
}

// Cycle through book-a-call images in order on page load
// Generic function that can be used for both header and footer images
function cycleBookACallImages(selector, storageKey, errorContext) {
  const $images = $(selector);
  if ($images.length === 0) {
    return;
  }

  const firstImageData = $images.first().attr('data-images');
  if (!firstImageData) {
    return;
  }

  try {
    const imagePaths = JSON.parse(firstImageData);
    const totalImages = imagePaths.length;
    if (totalImages === 0) {
      return;
    }

    let currentIndex = parseInt(localStorage.getItem(storageKey), 10) || 0;
    if (currentIndex >= totalImages) {
      currentIndex = 0;
    }

    $images.each(function () {
      try {
        const $img = $(this);
        const imagesData = $img.attr('data-images');

        if (!imagesData) {
          return;
        }

        const imgPaths = JSON.parse(imagesData);

        if (!Array.isArray(imgPaths) || imgPaths.length === 0) {
          return;
        }

        // All images are served from public/images; paths are used as-is
        const index = currentIndex % imgPaths.length;
        $img.attr({ src: imgPaths[index] });
      } catch (e) {
        // Silently skip this image if parsing fails, continue with others
        console.error(`Error updating ${errorContext} image:`, e);
      }
    });

    localStorage.setItem(storageKey, ((currentIndex + 1) % totalImages).toString());
  } catch (e) {
    console.error(`Error cycling ${errorContext} images:`, e);
  }
}

// Cycle through footer book-a-call images in order on page load
function cycleFooterImages() {
  cycleBookACallImages(
    '.footer__book-a-call-image[data-images]',
    'ally-footer-image-index',
    'footer'
  );
}

// Cycle through header book-a-call images in order on page load
function cycleHeaderImages() {
  cycleBookACallImages(
    '.header__book-a-call-image[data-images]',
    'ally-header-image-index',
    'header'
  );
}

// Cycle through accessibility-simplified images in order on page load
function cycleAccessibilitySimplifiedImages() {
  cycleBookACallImages(
    '.accessibility-simplified__image-img[data-images]',
    'ally-accessibility-simplified-image-index',
    'accessibility-simplified'
  );
}

$(() => {
  // Cycle footer, header, and accessibility-simplified images immediately on DOM ready, before other initialization
  cycleHeaderImages();
  cycleFooterImages();
  cycleAccessibilitySimplifiedImages();

  $('body').addClass('document-ready');

  // Dynamically import accessibility snapshot forms only if they exist on the page
  if ($('.accessibility-snapshot__form').length > 0) {
    import('@components/accessibility-snapshot/accessibility-snapshot.js').catch((err) => {
      console.error('Failed to load accessibility snapshot form module:', err);
    });
  }

  // Dynamically import ADA response form only if it exists on the page
  if ($('.ada-response-form__form').length > 0) {
    import('@components/ada-response-form/ada-response-form.js').catch((err) => {
      console.error('Failed to load ADA response form module:', err);
    });
  }

  if ($('.get-started-form__form').length > 0) {
    import('@components/get-started/get-started-form.js')
      .then(({ initGetStartedForms }) => {
        initGetStartedForms();
      })
      .catch((err) => {
        console.error('Failed to load Get Started form module:', err);
      });
  }

  if ($('.report-ax-barrier-form__form').length > 0) {
    import('@components/report-ax-barrier/report-ax-barrier-form.js')
      .then(({ initReportAxBarrierForms }) => {
        initReportAxBarrierForms();
      })
      .catch((err) => {
        console.error('Failed to load Report AX Barrier form module:', err);
      });
  }

  // Dynamically import web scanner form only if it exists on the page
  if ($('.web-scanner-form, [data-web-scanner-report]').length > 0) {
    import('@components/web-scanner-form/web-scanner-form.js').catch((err) => {
      console.error('Failed to load web scanner form module:', err);
    });
  }

  // Dynamically import career form only if it exists on the page
  if ($('.career-form__form').length > 0) {
    import('@components/career-form/career-form.js').catch((err) => {
      console.error('Failed to load career form module:', err);
    });
  }

  // Dynamically import insights listing only if it exists on the page
  if ($('.insights-listing').length > 0 || $('.insights-content').length > 0) {
    import('@components/insights-listing/insights-listing.js').catch((err) => {
      console.error('Failed to load insights listing module:', err);
    });
  }

  // Dynamically import insights generic only if it exists on the page
  if ($('.insights-generic').length > 0) {
    import('@components/insights-generic/insights-generic.js').catch((err) => {
      console.error('Failed to load insights generic module:', err);
    });
  }

  // Dynamically import certificate share actions only if the component is on the page
  if ($('.certificate-dark').length > 0) {
    import('@components/certificate-dark/certificate-dark.js')
      .then(({ initCertificateDark }) => initCertificateDark())
      .catch((err) => {
        console.error('Failed to load certificate dark module:', err);
      });
  }

  if ($('.ally.our-team').length > 0) {
    import('@components/our-team/our-team.js')
      .then(({ initOurTeamScroll }) => initOurTeamScroll())
      .catch((err) => {
        console.error('Failed to load our-team module:', err);
      });
  }

  $(window).on('load', () => {
    setTimeout(() => {
      processGridParaHeight();
    }, 100);
  });

  document.fonts.ready.then(() => {
    backgroundColorForHeading();
  });

  // Initialize cookie banner (async to handle geolocation check)
  initCookieBanner();

  watchForHeadingStyleChanges();
  headerMenuClick();
  imgToSvg();
  currentYear();
  activeNavigation();
  skipToMainContent();
  initCreditUnionsHashNavigation();
  initAdaLawsuitInPageNavigation();
  faqAccordion();
  processGridParaHeight();
  initAnalyticsLoader();
  initLiveChat();

  let resizeTimer;
  $(window).on('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      backgroundColorForHeading();
      processGridParaHeight();
    }, 250);
  });
});
